'use client';

import { c as _c } from "react/compiler-runtime";
import type { CodeEnvironmentCheck } from '@/lib/code-sessions/environment-suggestion';
import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { enabledUntilRefused } from './queries';
import { codeSessionRequest } from './runtime/cloud';
import { codeEnvironmentCheckUrl } from '@/lib/code-sessions/environment-suggestion';
type CodeEnvironmentCheckResponse = {
  check: CodeEnvironmentCheck | null;
};
export const codeEnvironmentCheckKeys = {
  /** Every check of a session's suggestions, as a prefix of each one's key. */
  session: (sessionId: string) => ['code-environment-check', sessionId] as const,
  detail: (sessionId: string, callId: string) => [...codeEnvironmentCheckKeys.session(sessionId), callId] as const
};

/**
 * A check can wait and run for half an hour or more. Most settle within
 * minutes, so its card reads it often at first, then less often.
 */
const CHECK_POLL = {
  earlyMs: 5_000,
  earlyForMs: 2 * 60_000,
  laterMs: 15_000
};

/** How long until a check's card reads it again: never once it settled. */
function checkPollInterval(check: CodeEnvironmentCheck | null | undefined): number | false {
  if (check?.status !== 'running') {
    return false;
  }
  return Date.now() - check.startedAt < CHECK_POLL.earlyForMs ? CHECK_POLL.earlyMs : CHECK_POLL.laterMs;
}

/** The latest check started from a suggestion's card, or null before one starts. */
export function codeEnvironmentCheckOptions(sessionId: string, callId: string) {
  return queryOptions({
    queryKey: codeEnvironmentCheckKeys.detail(sessionId, callId),
    queryFn: ({
      signal
    }) => codeSessionRequest<CodeEnvironmentCheckResponse>(`${codeEnvironmentCheckUrl(sessionId)}?${new URLSearchParams({
      callId
    })}`, {
      signal
    }),
    enabled: enabledUntilRefused(),
    // Worked out again after each read.
    refetchInterval: query => checkPollInterval(query.state.data?.check)
  });
}
export function useCodeEnvironmentCheck(sessionId, callId) {
  const $ = _c(3);
  let t0;
  if ($[0] !== callId || $[1] !== sessionId) {
    t0 = codeEnvironmentCheckOptions(sessionId, callId);
    $[0] = callId;
    $[1] = sessionId;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  return useQuery(t0);
}

/** Starts a check of the saved environment for a suggestion's card; the answer is its first read. */
export function useStartCodeEnvironmentCheck(sessionId, callId) {
  const $ = _c(10);
  const client = useQueryClient();
  let t0;
  if ($[0] !== callId || $[1] !== sessionId) {
    t0 = () => codeSessionRequest(codeEnvironmentCheckUrl(sessionId), {
      method: "POST",
      headers: {
        "content-type": "application/json"
      },
      body: JSON.stringify({
        callId
      })
    });
    $[0] = callId;
    $[1] = sessionId;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  let t1;
  if ($[3] !== callId || $[4] !== client || $[5] !== sessionId) {
    t1 = async t2 => {
      const {
        check
      } = t2;
      const queryKey = codeEnvironmentCheckKeys.detail(sessionId, callId);
      await client.cancelQueries({
        queryKey,
        exact: true
      });
      client.setQueryData(queryKey, {
        check
      });
    };
    $[3] = callId;
    $[4] = client;
    $[5] = sessionId;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[7] !== t0 || $[8] !== t1) {
    t2 = {
      retry: false,
      mutationFn: t0,
      onSuccess: t1
    };
    $[7] = t0;
    $[8] = t1;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  return useMutation(t2);
}